import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';
import { fmt, TotalCard } from './shared';

const FinanceAnnualPage = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAnnual = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/finance/annual?year=${year}`);
      setData(res.data);
    } catch {
      setError(t('annualDataLoadError'));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchAnnual();
  }, [fetchAnnual]);

  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const revenueLabel = t('revenue');
  const chargesLabel = t('charges');
  const profitLabel = t('profit');
  const chartData = data?.months?.map((m) => ({
    name: m.label.substring(0, 3),
    [revenueLabel]: m.total_revenue,
    [chargesLabel]: m.total_charges,
    [profitLabel]: m.profit,
  })) || [];

  const totals = data?.totals || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-foreground">{t('annualView')}</h2>
        <div className="flex gap-1 flex-wrap">
          {years.map((y) => (
            <Button
              key={y}
              size="sm"
              variant={y === year ? 'default' : 'outline'}
              onClick={() => setYear(y)}
            >
              {y}
            </Button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={fetchAnnual}>
          <RefreshCw size={14} className="mr-1" />
          {t('refresh')}
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
      ) : (
        <>
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base">{t('revenue')} / {t('charges')} / {t('profit')} — {year}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#8E8A82' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#8E8A82' }} tickFormatter={(v) => `${v}€`} />
                  <Tooltip formatter={(value) => fmt(value)} contentStyle={{ borderRadius: '8px', border: '1px solid #ECE7DC', fontSize: '13px' }} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey={revenueLabel} fill="#E8721A" radius={[3, 3, 0, 0]} barSize={28} />
                  <Bar dataKey={chargesLabel} fill="#15140F" radius={[3, 3, 0, 0]} barSize={28} opacity={0.85} />
                  <Line type="monotone" dataKey={profitLabel} stroke="#0E8855" strokeWidth={2.5} dot={{ r: 4, fill: '#fff', stroke: '#0E8855', strokeWidth: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {data?.months && data.months.length > 0 && (
            <DataTable
              data={data.months}
              searchPlaceholder={t('searchMonth')}
              emptyMessage={t('noData')}
              pageSize={12}
              columns={[
                {
                  accessorKey: 'label',
                  header: t('month'),
                  meta: { cellClassName: 'font-medium text-foreground' },
                },
                {
                  accessorKey: 'total_revenue',
                  header: t('revenue'),
                  cell: ({ getValue }) => <span className="text-foreground">{fmt(getValue())}</span>,
                  meta: { className: 'text-right', cellClassName: 'text-right' },
                },
                {
                  accessorKey: 'total_charges',
                  header: t('charges'),
                  cell: ({ getValue }) => <span className="text-foreground">{fmt(getValue())}</span>,
                  meta: { className: 'text-right', cellClassName: 'text-right' },
                },
                {
                  accessorKey: 'profit',
                  header: t('profit'),
                  cell: ({ getValue }) => {
                    const v = getValue();
                    return <span className={`font-semibold ${v >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(v)}</span>;
                  },
                  meta: { className: 'text-right', cellClassName: 'text-right' },
                },
                {
                  accessorKey: 'carreraisons_count',
                  header: t('shipments'),
                  meta: { className: 'text-right', cellClassName: 'text-right text-muted-foreground' },
                },
              ]}
            />
          )}

          {totals && (
            <div>
              <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wide">
                {t('annualTotals')} {year}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                <TotalCard label={t('totalRevenue')} value={fmt(totals.total_revenue)} />
                <TotalCard label={t('totalCharges')} value={fmt(totals.total_charges)} />
                <TotalCard
                  label={t('totalProfit')}
                  value={fmt(totals.profit)}
                  color={totals.profit >= 0 ? 'green' : 'red'}
                />
                <TotalCard
                  label={t('totalDebt')}
                  value={fmt(totals.total_debt)}
                  color={totals.total_debt > 0 ? 'amber' : 'slate'}
                />
                <TotalCard label={t('shipmentsCount')} value={totals.carreraisons_count || 0} />
                <TotalCard
                  label={t('profitableOnes')}
                  value={`${totals.profitable_count || 0} / ${totals.carreraisons_count || 0}`}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FinanceAnnualPage;
